import { Spin } from 'antd'
import Avatar from 'boring-avatars'
import { useRef, useState } from 'react'

import AddAccountIcon from '@/assets/icons/add-account.svg?react'
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import ExternalLinkIcon from '@/assets/icons/external-link.svg?react'
import MinusIcon from '@/assets/icons/minus.svg?react'
import SwitchIcon from '@/assets/icons/switch.svg?react'
import { message, Trans } from '@/components'
import { Divider } from '@/components/base/Divider'
import { Loading } from '@/components/base/Loading'
import { cn } from '@/utils/style'

import { UserDropdownItem } from './UserDropdownItem'

type Account = {
  id: number
  name: string
}

interface AccountSwitcherProps {
  accounts: Account[]
  activeAccountId: number | null
  onAddAccount: () => void
  onSwitchAccount: (accountId: number) => Promise<void>
  onRemoveAccount: (accountId: number) => Promise<void>
}

export function AccountSwitcher({
  accounts,
  activeAccountId,
  onAddAccount,
  onSwitchAccount,
  onRemoveAccount,
}: AccountSwitcherProps) {
  const [showAccountsMenu, setShowAccountsMenu] = useState(false)
  const [switchingAccount, setSwitchingAccount] = useState<number | null>(null)
  const [removingAccount, setRemovingAccount] = useState<number | null>(null)
  const accountsTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const otherAccounts = accounts.filter((acc) => acc.id !== activeAccountId)

  const handleSwitchAccount = async (accountId: number) => {
    if (switchingAccount === accountId) return

    try {
      setSwitchingAccount(accountId)

      // Clear team context if switching account (force back to platform)
      const { useTeamStore } = await import('@/stores/team')
      const teamStore = useTeamStore.getState()
      if (teamStore.teamTokenData) {
        teamStore.clearTeamContext()
      }

      await onSwitchAccount(accountId)

      // Redirect to platform home page
      window.location.href = '/create'
    } catch (error) {
      console.error('Failed to switch account:', error)
      message.error('header.userDropdown.switchAccountError')
      setSwitchingAccount(null)
    }
  }

  const handleRemoveAccount = async (accountId: number, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (removingAccount === accountId) return

    try {
      setRemovingAccount(accountId)
      await onRemoveAccount(accountId)
    } catch (error) {
      console.error('Failed to remove account:', error)
      message.error('Failed to remove account. Please try again.')
    } finally {
      setRemovingAccount(null)
    }
  }

  const handleAddAccountInMenu = () => {
    onAddAccount()
    setShowAccountsMenu(false)
  }

  const handleAccountsMouseEnter = () => {
    if (accountsTimeoutRef.current) {
      clearTimeout(accountsTimeoutRef.current)
    }
    setShowAccountsMenu(true)
  }

  const handleAccountsMouseLeave = () => {
    accountsTimeoutRef.current = setTimeout(() => {
      setShowAccountsMenu(false)
    }, 100)
  }

  return (
    <div
      className="relative"
      onMouseEnter={handleAccountsMouseEnter}
      onMouseLeave={handleAccountsMouseLeave}
    >
      <UserDropdownItem
        icon={SwitchIcon}
        label={<Trans i18nKey="header.userDropdown.switchAccount">Switch Account</Trans>}
        active={showAccountsMenu}
        trailing={<ArrowRightIcon className="h-4 w-4 shrink-0 text-tertiary" />}
      />

      {/* Switch Account Secondary Menu */}
      <div
        className={cn(
          'absolute top-0 -left-4 flex max-h-[420px] w-[287px] -translate-x-full flex-col gap-2 overflow-hidden rounded-xl bg-panel-white py-4 shadow-card-100',
          showAccountsMenu ? 'flex' : 'hidden',
        )}
      >
        {switchingAccount ? (
          // Show loading state when switching
          <div className="flex items-center justify-center px-4 py-8">
            <Loading className="h-8 w-8" />
            <span className="t-sm ml-3 text-secondary">
              <Trans i18nKey="header.userDropdown.switchingAccount">Switching account...</Trans>
            </span>
          </div>
        ) : (
          <>
            {otherAccounts.length > 0 && (
              <>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {otherAccounts.map((account) => {
                    const isRemoving = removingAccount === account.id

                    return (
                      <div
                        key={account.id}
                        className={cn(
                          'flex items-center gap-x-4 px-4 py-2 transition-colors',
                          isRemoving
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer hover:bg-panel-gray-100',
                        )}
                        onClick={() => !isRemoving && handleSwitchAccount(account.id)}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-x-3">
                          <Avatar
                            size={32}
                            name={account.name.split('@')[0]}
                            variant="beam"
                            colors={['#525866']}
                          />
                          <div className="t-x-sb min-w-0 flex-1 truncate">
                            {account.name.split('@')[0]}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-x-1">
                          {isRemoving && <Spin size="small" />}
                          <button
                            onClick={(e) => !isRemoving && handleRemoveAccount(account.id, e)}
                            className={cn(
                              'flex',
                              isRemoving
                                ? 'cursor-not-allowed opacity-25'
                                : 'opacity-50 hover:opacity-100',
                            )}
                            disabled={isRemoving}
                          >
                            <MinusIcon className="h-4 w-4 text-tertiary" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <Divider className="my-0" />
              </>
            )}

            {/* Add Account in Secondary Menu */}
            <div className="px-4">
              <UserDropdownItem
                icon={AddAccountIcon}
                label={<Trans i18nKey="header.userDropdown.addAccount">Add Account</Trans>}
                trailing={<ExternalLinkIcon className="h-4 w-4 shrink-0 text-tertiary" />}
                onClick={handleAddAccountInMenu}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
